import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  SEO_DOMAIN,
  buildCanonicalUrl,
  parsePathname,
  PAGE_NAMES,
  type PageKey,
} from "@/lib/seo";

// Schema ID for cleanup
const STRUCTURED_DATA_ID = "structured-data-seo";

// FAQ question keys for FAQPage schema
const FAQ_QUESTIONS = [
  "whatServices",
  "whoCanPartner",
  "howToEngage",
  "procurement",
  "pricing",
  "dataProtection",
  "certifications",
  "deployment",
  "languages",
  "individuals",
];

interface WebPageSchema {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  url: string;
  inLanguage: string;
  isPartOf: {
    "@type": string;
    name: string;
    url: string;
  };
}

interface BreadcrumbSchema {
  "@context": string;
  "@type": string;
  itemListElement: Array<{
    "@type": string;
    position: number;
    name: string;
    item?: string;
  }>;
}

interface FAQPageSchema {
  "@context": string;
  "@type": string;
  mainEntity: Array<{
    "@type": string;
    name: string;
    acceptedAnswer: {
      "@type": string;
      text: string;
    };
  }>;
}

// Build WebPage schema
function buildWebPageSchema(
  title: string,
  description: string,
  canonicalUrl: string,
  lang: string
): WebPageSchema {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description: description,
    url: canonicalUrl,
    inLanguage: lang,
    isPartOf: {
      "@type": "WebSite",
      name: "VisExperience",
      url: SEO_DOMAIN,
    },
  };
}

// Build BreadcrumbList schema
function buildBreadcrumbSchema(
  pageKey: PageKey,
  canonicalUrl: string,
  pageName: string,
  lang: string
): BreadcrumbSchema {
  const homeUrl = lang === "en" ? SEO_DOMAIN : `${SEO_DOMAIN}/${lang}`;

  const items: BreadcrumbSchema["itemListElement"] = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: homeUrl,
    },
  ];

  // Add current page if not home
  if (pageKey !== "home") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: pageName,
      item: canonicalUrl,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// Build FAQPage schema
function buildFAQPageSchema(t: (key: string) => string): FAQPageSchema {
  const mainEntity = FAQ_QUESTIONS.map((qKey) => ({
    "@type": "Question" as const,
    name: t(`faq.questions.${qKey}`),
    acceptedAnswer: {
      "@type": "Answer" as const,
      text: t(`faq.questions.${qKey}Answer`),
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

// Inject structured data into the page
function injectStructuredData(schemas: object[]): void {
  // Remove existing structured data
  document
    .querySelectorAll(`script[data-seo-id="${STRUCTURED_DATA_ID}"]`)
    .forEach((el) => {
      el.remove();
    });

  // Create new script tag with all schemas
  schemas.forEach((schema, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-id", STRUCTURED_DATA_ID);
    script.setAttribute("data-schema-index", index.toString());
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export function useStructuredData() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Parse current path using centralized utility
    const { lang: currentLang, pagePath, pageKey } = parsePathname(
      location.pathname,
      i18n.language
    );

    // Get translated title and description
    const title = t(`seo.${pageKey}.title`, { defaultValue: "" });
    const description = t(`seo.${pageKey}.description`, { defaultValue: "" });

    // Build canonical URL
    const canonicalUrl = buildCanonicalUrl(currentLang, pagePath);

    // Get page name for breadcrumbs
    const pageName = PAGE_NAMES[pageKey] || "Page";

    // Build schemas array
    const schemas: object[] = [];

    // Add WebPage schema
    if (title && description) {
      schemas.push(
        buildWebPageSchema(title, description, canonicalUrl, currentLang)
      );
    }

    // Add BreadcrumbList schema
    schemas.push(
      buildBreadcrumbSchema(pageKey, canonicalUrl, pageName, currentLang)
    );

    // Add FAQPage schema only for FAQ page
    if (pageKey === "faq") {
      schemas.push(buildFAQPageSchema(t));
    }

    // Inject all schemas
    injectStructuredData(schemas);

    // Cleanup on unmount
    return () => {
      document
        .querySelectorAll(`script[data-seo-id="${STRUCTURED_DATA_ID}"]`)
        .forEach((el) => {
          el.remove();
        });
    };
  }, [location.pathname, t, i18n.language]);
}
